/* Modèle de la Synthèse financière — calcul pur partagé entre l'onglet
   (FinancialsSynthesisTab) et l'impression (FinancialsSynthesisPrint).
   Extrait verbatim du useMemo de l'onglet : aucun chiffre fabriqué, donnée
   absente → null. Entrées = vues bilan/P&L (N, N-1, N-2), capacité, indirects,
   ligne de crédit, année. */

import { contractActiveInMonth } from '../hr/salaryMass';
import { p, tp } from '../../i18n/useP';

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'].map((mo) => p(mo));
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const monthsArr = (obj) => Array.from({ length: 12 }, (_, m) => num(obj?.[String(m + 1)]));
// Lignes standard du bilan : retrouvées par leur CLÉ (std_key), jamais par leur
// libellé — renommer une ligne ne doit pas casser un KPI.
const findStd = (rows, key) => (rows || []).find((r) => r.type === 'detail' && r.std_key === key) || null;
// ⚠️ Reste par libellé : « Passifs transitoires » et « Produits encaissés d'avance »
// n'ont pas de clé standard (lignes propres au tenant). Basculer sur `accruals_other`
// changerait le BFRE affiché → décision métier, pas un refactor. Voir
// WIP/refonte-finance-structure.md §6.
const findDetail = (rows, re) => (rows || []).find((r) => r.type === 'detail' && re.test(r.label || '')) || null;
const findCode = (rows, code) => (rows || []).find((r) => r.code === code) || null;

/* KPI « Liquidité & soutenabilité » (6 postes) au dernier mois clôturé + valeurs
   du mois précédent, pour afficher une variation courte. Réutilise la même logique
   que buildSynthesisModel (solde bilan du mois ; burn = salaires+charges du mois
   au P&L + 1/12 des charges indirectes). Aucun chiffre fabriqué → null si absent. */
export function liquiditySustainability({ bs, pl, indirect, closed }) {
  if (!bs || !pl || !closed) return null;
  const bsRows = bs.rows || [];
  const plRows = pl.rows || [];
  const liqRow = findStd(bsRows, 'liquidity');
  const recRow = findStd(bsRows, 'ar_clients');
  const apRow = findStd(bsRows, 'ap_suppliers');
  const advRow = findStd(bsRows, 'customer_advances');
  const invRow = findStd(bsRows, 'inventory');
  const roleOf = {};
  (pl.lines || []).forEach((l) => { roleOf[l.id] = l.line_role; });
  const hasIndirect = Array.isArray(indirect) && indirect.length > 0;
  const indirectMonthly = (indirect || []).reduce(
    (a, c) => a + (c.mode === 'monthly' ? num(c.amount) : num(c.amount) / 12), 0,
  );

  const monthVal = (row, m) => {
    const v = row?.months?.[String(m)];
    return Number.isFinite(Number(v)) ? Number(v) : null;
  };
  const ytdVal = (row) => (row && Number.isFinite(Number(row.total_ytd)) ? Number(row.total_ytd) : null);
  const payrollAt = (m) => {
    let p = 0; let has = false;
    plRows.forEach((r) => {
      if (r.type === 'detail' && (roleOf[r.code] === 'salary' || roleOf[r.code] === 'social_charges')) {
        const v = r.months?.[String(m)];
        if (Number.isFinite(Number(v))) { p += Math.abs(Number(v)); has = true; }
      }
    });
    return has ? p : null;
  };
  const kpisAt = (m, useYtd) => {
    const bsVal = (row) => (useYtd ? ytdVal(row) : monthVal(row, m));
    const liq = bsVal(liqRow);
    const pay = payrollAt(m);
    let burn = null; let runway = null;
    if (pay != null || hasIndirect) {
      burn = (pay || 0) + indirectMonthly;
      if (burn > 0 && liq != null) runway = liq / burn;
    }
    return { liq, rec: bsVal(recRow), inv: bsVal(invRow), adv: bsVal(advRow), ap: bsVal(apRow), burn, runway };
  };

  // Ouverture de trésorerie (solde au 31.12.N-1) pour la variation YTD.
  const liqOpen = liqRow && Number.isFinite(Number(liqRow.prev_dec)) ? Number(liqRow.prev_dec) : null;

  // Séries mensuelles réalisées (indice 0 = ouverture 31.12.N-1, puis mois
  // 1..closed) pour les courbes de fond du bandeau « Évolution de la liquidité ».
  // Poste absent → série null (aucun chiffre fabriqué). BFRE par mois =
  // créances + stocks − dettes fournisseurs − acomptes (mêmes postes que la tuile).
  const openOf = (row) => (row && Number.isFinite(Number(row.prev_dec)) ? Number(row.prev_dec) : null);
  const serieOf = (row) => {
    if (!row) return null;
    const out = [openOf(row)];
    for (let m = 1; m <= closed; m++) out.push(monthVal(row, m));
    return out;
  };
  const sLiq = serieOf(liqRow);
  const sRec = serieOf(recRow);
  const sInv = serieOf(invRow);
  const sAdv = serieOf(advRow);
  const sAp = serieOf(apRow);
  const sBfre = (sRec || sInv || sAdv || sAp)
    ? Array.from({ length: closed + 1 }, (_, i) => num(sRec?.[i]) + num(sInv?.[i]) - num(sAp?.[i]) - num(sAdv?.[i]))
    : null;
  // BFRE + Liquidités (même lecture que la ligne totale de l'onglet BFRE).
  const sBfreLiq = (sBfre && sLiq)
    ? Array.from({ length: closed + 1 }, (_, i) => (sLiq[i] == null ? null : sLiq[i] + sBfre[i]))
    : null;
  const series = { liq: sLiq, rec: sRec, inv: sInv, adv: sAdv, ap: sAp, bfre: sBfre, bfreLiq: sBfreLiq };
  const open = {
    liq: liqOpen, rec: openOf(recRow), inv: openOf(invRow), adv: openOf(advRow), ap: openOf(apRow),
    bfre: sBfre ? sBfre[0] : null,
    bfreLiq: sBfreLiq ? sBfreLiq[0] : null,
  };
  return { cur: kpisAt(closed, true), prev: closed > 1 ? kpisAt(closed - 1, false) : null, liqOpen, series, open };
}

export function buildSynthesisModel({ bs, bsPrev, pl, plPrev, plPrev2, cap, indirect, contracts, creditLimit, year }) {
    if (!bs || !pl) return null;
    const bsRows = bs.rows || [];
    const plRows = pl.rows || [];
    const closed = Number.isFinite(pl.closed_count) ? pl.closed_count : 12;

    // ── Bilan : postes (snapshot = total_ytd au dernier mois clôturé) ──────
    const liqRow = findStd(bsRows, 'liquidity');
    const recRow = findStd(bsRows, 'ar_clients');
    const stockRow = findStd(bsRows, 'inventory');
    const apRow = findStd(bsRows, 'ap_suppliers');
    const advRow = findStd(bsRows, 'customer_advances');
    const accrRow = findDetail(bsRows, /passifs transitoires/i);
    const deferRow = findDetail(bsRows, /encaiss.*avance|produits.*avance/i);
    const curAssets = findCode(bsRows, 'sec:current_assets');
    const curLiab = findCode(bsRows, 'sec:current_liabilities');

    const snap = (row) => (row ? num(row.total_ytd) : null);
    const liq = snap(liqRow);
    const rec = snap(recRow);
    const stock = snap(stockRow);
    const ap = snap(apRow);
    const adv = snap(advRow);
    const accr = snap(accrRow);
    const defer = snap(deferRow);
    const ca = snap(curAssets);
    const cl = snap(curLiab);

    // BFRE = créances + stocks − dettes fourn. − passifs transitoires − produits encaissés d'avance
    const bfreParts = [rec, stock, ap == null ? null : -ap, accr == null ? null : -accr, defer == null ? null : -defer];
    const bfre = bfreParts.some((v) => v != null)
      ? bfreParts.reduce((a, v) => a + num(v), 0)
      : null;
    const tresoBfre = liq != null && bfre != null ? liq + bfre : null;

    // ── Liquidité mensuelle (réel) pour le graphe ─────────────────────────
    const liqMonthly = liqRow ? monthsArr(liqRow.months).map((v, i) => (i < closed ? v : null)) : null;

    // ── Liquidité mensuelle N-1 (traitillé, comparatif) ───────────────────
    //    Mois non saisi → null (pas 0) : la ligne pointillée s'interrompt au
    //    lieu de plonger à zéro. Aucun chiffre fabriqué.
    const liqRowPrev = bsPrev ? findStd(bsPrev.rows || [], 'liquidity') : null;
    const liqMonthlyPrev = liqRowPrev
      ? Array.from({ length: 12 }, (_, i) => {
        const raw = liqRowPrev.months?.[String(i + 1)];
        return Number.isFinite(Number(raw)) ? Number(raw) : null;
      })
      : null;

    // ── Burn rate & runway — CHARGE MENSUELLE FIXE (cash) :
    //    · Salaires : run-rate CONTRACTUEL du dernier mois clôturé (base + 13e/14e
    //      pondéré par nb_of_salary). La compta mensuelle ne convient pas (le 13e y
    //      est provisionné/ponctuel) → on lit les contrats.
    //    · Charges sociales : taux effectif (P&L) appliqué au salaire contractuel.
    //    · Autres charges de personnel + autres charges d'exploitation : moy. YTD.
    //    On ne mêle PAS la variation de trésorerie. Runway = solde / burn.
    const roleOf = {};
    (pl.lines || []).forEach((l) => { roleOf[l.id] = l.line_role; });
    const mIdx = Math.max(0, closed - 1);
    let salBaseM = 0; let salUpliftM = 0;                         // base + 13e/14e (contrats, mois clôturé)
    (contracts || []).forEach((c) => {
      if (!contractActiveInMonth(c, year, mIdx)) return;
      const ms = num(c.monthly_salary);
      salBaseM += ms;
      salUpliftM += ms * (Math.max(0, num(c.nb_of_salary || 12) - 12) / 12);
    });
    const salTotalM = salBaseM + salUpliftM;
    // Taux de charges sociales effectif (P&L YTD) + autres charges de personnel.
    let salaryYtd = 0; let socialYtd = 0; let otherPersYtd = 0; let hasPayroll = false;
    plRows.forEach((r) => {
      if (r.type !== 'detail' || r.section !== 'personnel') return;
      hasPayroll = true;
      const role = roleOf[r.code];
      if (role === 'salary' || role === 'bonus') salaryYtd += Math.abs(num(r.total_ytd));
      else if (role === 'social_charges') socialYtd += Math.abs(num(r.total_ytd));
      else otherPersYtd += Math.abs(num(r.total_ytd));           // indemnités, provisions, formations…
    });
    const socialRate = salaryYtd > 0 ? socialYtd / salaryYtd : 0;
    const socialM = salTotalM * socialRate;
    const otherPersM = closed > 0 ? otherPersYtd / closed : 0;
    const opexRowB = findCode(plRows, 'sec:other_opex');
    const opexM = (opexRowB && closed > 0) ? Math.abs(num(opexRowB.total_ytd)) / closed : 0;
    const payroll = salTotalM + socialM;                          // masse salariale (signal « mois de salaire »)
    let burn = null; let runway = null;
    let burnComps = null;
    if (salTotalM > 0 || hasPayroll || opexM > 0) {
      burn = salTotalM + socialM + otherPersM + opexM;
      if (burn > 0 && liq != null) runway = liq / burn;
      const socialPct = salTotalM > 0 ? Math.round(socialRate * 100) : null;
      const moLbl = MONTHS[mIdx];
      burnComps = [
        [tp('phrases:p_d0283e2f15', { month: moLbl }), salBaseM],
        [p('13ᵉ / 14ᵉ salaire (pondéré · contrats)'), salUpliftM],
        [socialPct != null ? tp('phrases:p_5202a9f6d8', { pct: socialPct }) : p('Charges sociales'), socialM],
        [p('Autres charges de personnel (moy. YTD)'), otherPersM],
        [p("Autres charges d'exploitation (moy. YTD)"), opexM],
      ].filter((c) => Math.round(c[1]) !== 0);
    }
    const liqReal = (liqMonthly || []).filter((v) => Number.isFinite(v));

    // ── P&L : revenus / EBITDA / annualisation ────────────────────────────
    const revRow = findCode(plRows, 'sec:revenue');
    const ebitdaRow = findCode(plRows, 'ebitda');
    const cogsRow = findCode(plRows, 'sec:cogs');
    const opexRow = findCode(plRows, 'sec:other_opex');
    const revYtd = revRow ? num(revRow.total_ytd) : 0;
    const cogsYtd = cogsRow ? num(cogsRow.total_ytd) : 0;
    const opexYtd = opexRow ? num(opexRow.total_ytd) : 0;
    const ann = (ytd) => (closed > 0 ? (ytd / closed) * 12 : 0);
    const revAnn = ann(revYtd);
    const purchasesAnn = Math.abs(ann(cogsYtd + opexYtd));
    const cogsAnn = Math.abs(ann(cogsYtd));

    // ── Rentabilité (réel, finance pure) : marges + annualisation ─────────
    //    Marges en % du CA YTD (base comparable, pas besoin d'annualiser un ratio).
    //    Montants annualisés (run rate) pour le CA et le résultat net.
    const grossRow = findCode(plRows, 'gross_margin');
    const netRow = findCode(plRows, 'net_result');
    const grossYtd = grossRow ? num(grossRow.total_ytd) : null;
    const ebitdaYtd = ebitdaRow ? num(ebitdaRow.total_ytd) : null;
    const netYtd = netRow ? num(netRow.total_ytd) : null;
    const margeBrute = grossYtd != null && revYtd ? grossYtd / revYtd : null;
    const margeEbitda = ebitdaYtd != null && revYtd ? ebitdaYtd / revYtd : null;
    const margeNette = netYtd != null && revYtd ? netYtd / revYtd : null;
    const netAnn = netYtd != null ? ann(netYtd) : null;
    // Croissance CA : YTD N vs même période N-1 (mois 1..closed), pas l'année pleine.
    // caCumDelta = écart absolu YTD. revPrevFull = CA année pleine N-1 (réf. de l'annualisé).
    let caGrowth = null;
    let caCumDelta = null;
    let revPrevFull = null;
    let revAnnSeason = null;   // annualisé corrigé de la saisonnalité N-1
    if (plPrev) {
      const revRowP = findCode(plPrev.rows || [], 'sec:revenue');
      if (revRowP) {
        let revPytd = 0;
        for (let mo = 1; mo <= closed; mo++) revPytd += num(revRowP.months?.[String(mo)]);
        revPrevFull = monthsArr(revRowP.months).reduce((a, b) => a + b, 0);
        if (revPytd) {
          caGrowth = revYtd / revPytd - 1;
          caCumDelta = revYtd - revPytd;
          // Projection plein exercice via la forme saisonnière de N-1 :
          // CA_N(plein) = CA_N(YTD) × CA_{N-1}(plein) / CA_{N-1}(même période).
          revAnnSeason = revYtd * revPrevFull / revPytd;
        }
      }
    }
    // Tuile CA annualisé : saisonnalité N-1 si dispo, sinon annualisation linéaire (étiquetée).
    const caAnnDisplay    = revAnnSeason != null ? revAnnSeason : revAnn;
    const caAnnIsSeasonal = revAnnSeason != null;
    const annGrowth = revPrevFull ? caAnnDisplay / revPrevFull - 1 : null;
    const annDelta  = revPrevFull != null ? caAnnDisplay - revPrevFull : null;

    // ── Valeur ajoutée + marges annualisées (CHF) pour les tuiles ─────────
    const vaRow = findCode(plRows, 'value_added');
    const vaYtd = vaRow ? num(vaRow.total_ytd) : null;
    const margeVA = vaYtd != null && revYtd ? vaYtd / revYtd : null;
    // Marge brute en % de la valeur ajoutée (lecture complémentaire au % du CA).
    const margeBruteVA = grossYtd != null && vaYtd ? grossYtd / vaYtd : null;
    const vaAnn = vaYtd != null ? ann(vaYtd) : null;
    const grossAnn = grossYtd != null ? ann(grossYtd) : null;
    const ebitdaAnn = ebitdaYtd != null ? ann(ebitdaYtd) : null;

    // ── Revenus mensuels N vs N-1 (bar) + revenus cumulés (courbe) ────────
    const revRowPfull = plPrev ? findCode(plPrev.rows || [], 'sec:revenue') : null;
    const revMN = revRow ? monthsArr(revRow.months) : Array(12).fill(0);
    const revMP = revRowPfull ? monthsArr(revRowPfull.months) : null;
    const revBars = MONTHS.map((mo, i) => ({
      month: mo,
      n: i < closed ? revMN[i] : null,
      prev: revMP ? revMP[i] : null,
    }));
    let cumN = 0; let cumP = 0;
    const revCum = MONTHS.map((mo, i) => {
      cumN += i < closed ? revMN[i] : 0;
      cumP += revMP ? revMP[i] : 0;
      return { month: mo, n: i < closed ? cumN : null, prev: revMP ? cumP : null };
    });
    const hasPrevRev = !!revMP;

    // ── CA / EPT (annualisé) vs N-1 ───────────────────────────────────────
    const eptReal = cap?.columns?.real?.ept_moyen ?? null;
    const eptPrev = cap?.columns?.prev?.ept_moyen ?? null;
    const capFte = cap?.columns?.real?.cap_fte ?? null;
    const caEpt = eptReal ? revAnn / eptReal : null;
    // CA cumulé (YTD réel) et CA cumulé rapporté à la capacité (EPT capa)
    const caCum = revRow ? revYtd : null;
    const caCumEptCap = caCum != null && capFte ? caCum / capFte : null;
    let caEptPrev = null;
    if (plPrev && eptPrev) {
      const revRowP = findCode(plPrev.rows || [], 'sec:revenue');
      const revP = revRowP ? num(revRowP.total) : 0;
      caEptPrev = revP / eptPrev;
    }
    const caEptDelta = caEpt != null && caEptPrev ? (caEpt / caEptPrev - 1) * 100 : null;

    // ── Ratios de liquidité (degrés 1/2/3) ────────────────────────────────
    const r1 = cl ? num(liq) / cl : null;                       // immédiate (cash)
    const r2 = cl ? (num(liq) + num(rec)) / cl : null;          // réduite (quick)
    const r3 = cl ? num(ca) / cl : null;                        // générale (current)

    // ── Délais du cycle (jours, annualisés) ───────────────────────────────
    const dso = rec != null && revAnn > 0 ? (rec / revAnn) * 365 : null;
    const dio = stock != null && cogsAnn > 0 ? (stock / cogsAnn) * 365 : null;
    const dpo = ap != null && purchasesAnn > 0 ? (ap / purchasesAnn) * 365 : null;

    // ── Revenus & charges — 12 mois GLISSANTS (run rate jusqu'à l'EBITDA) ──
    //    Fenêtre = 12 derniers mois clôturés ; chaque point = somme des 12 mois
    //    qui le précèdent → on remonte ~24 mois (N-1 et N-2). Charges op. =
    //    revenus − EBITDA (donc périmètre jusqu'à l'EBITDA, hors amortissements).
    let rolling = null;
    {
      const yrViews = [plPrev2, plPrev, pl];              // N-2, N-1, N (chrono)
      const baseYear = year - 2;
      const rev36 = []; const eb36 = []; const va36 = []; const real36 = [];
      yrViews.forEach((v) => {
        const rr = v ? findCode(v.rows || [], 'sec:revenue') : null;
        const er = v ? findCode(v.rows || [], 'ebitda') : null;
        const vr = v ? findCode(v.rows || [], 'value_added') : null;
        const rArr = rr ? monthsArr(rr.months) : Array(12).fill(0);
        const eArr = er ? monthsArr(er.months) : Array(12).fill(0);
        const vArr = vr ? monthsArr(vr.months) : Array(12).fill(0);
        const cc = v && Number.isFinite(v.closed_count) ? v.closed_count : 0;
        for (let mo = 0; mo < 12; mo++) {
          rev36.push(rArr[mo]); eb36.push(eArr[mo]); va36.push(vArr[mo]); real36.push(mo < cc);
        }
      });
      const lastIdx = 24 + Math.max(0, closed - 1);        // dernier mois clôturé de N
      // Fenêtre glissante de 12 pts terminant à `endIdx` (chaque pt = cumul 12 mois).
      const buildPts = (endIdx) => {
        const out = [];
        for (let j = endIdx - 11; j <= endIdx; j++) {
          if (j < 0) continue;
          const gy = baseYear + Math.floor(j / 12);
          const label = `${MONTHS[j % 12]} ${String(gy).slice(2)}`;
          let allReal = true;
          for (let k = j - 11; k <= j; k++) { if (k < 0 || !real36[k]) { allReal = false; break; } }
          if (!allReal) { out.push({ month: label, rev: null, op: null, va: null }); continue; }
          let sr = 0; let so = 0; let sv = 0;
          for (let k = j - 11; k <= j; k++) { sr += rev36[k]; so += rev36[k] - eb36[k]; sv += va36[k]; }
          out.push({ month: label, rev: sr, op: so, va: sv });
        }
        return out;
      };
      const pts = buildPts(lastIdx);
      if (pts.filter((p) => Number.isFinite(p.rev)).length >= 2) rolling = pts;
    }
    // CA 12 mois roulant = dernier point valide de la courbe glissante.
    const ca12m = rolling ? ([...rolling].reverse().find((p) => Number.isFinite(p.rev))?.rev ?? null) : null;

    // Composition BFRE — 12 mois glissants RÉALISÉS (bilans N + N-1, aucun
    // prévisionnel). Postes = créances + stocks − dettes fourn. − acomptes.
    const bfreStackRolling = (() => {
      if (!bsPrev && closed < 12) return null;                  // pas d'historique N-1
      const serieOf = (view, stdKey) => {
        const l = (view?.lines || []).find((x) => x.std_key === stdKey);
        if (!l) return Array(12).fill(0);
        const r = (view?.rows || []).find((x) => x.code === l.id);
        return monthsArr(r?.months);
      };
      const cats = [
        { label: tp('phrases:p_036e9d357b'), key: 'ar_clients', color: 'var(--color-accent)', neg: false },
        { label: p('Stocks'), key: 'inventory', color: 'var(--color-sage)', neg: false },
        { label: p('Dettes fournisseurs'), key: 'ap_suppliers', color: 'var(--color-amber)', neg: true },
        { label: p('Acomptes clients'), key: 'customer_advances', color: 'var(--color-gold)', neg: true },
      ];
      const lastIdx = 12 + Math.max(0, closed - 1);             // 0..11 = N-1, 12..23 = N
      const idxs = [];
      for (let j = lastIdx - 11; j <= lastIdx; j++) idxs.push(j);
      const stack = cats.map((c) => {
        const s24 = serieOf(bsPrev, c.key).concat(serieOf(bs, c.key));
        return { label: c.label, color: c.color, neg: c.neg, m: idxs.map((j) => (j >= 0 ? Math.abs(num(s24[j])) : 0)) };
      });
      const labels = idxs.map((j) => MONTHS[((j % 12) + 12) % 12]);
      return { stack, labels };
    })();

    // Burn N-1 / N-2 (charge mensuelle comparable) = charges fixes annuelles
    // (personnel + autres charges d'exploitation) ÷ 12, pour l'historique du burn.
    const annualFixedMonthly = (v) => {
      if (!v) return null;
      const rows = v.rows || [];
      const per = findCode(rows, 'sec:personnel');
      const opx = findCode(rows, 'sec:other_opex');
      if (!per && !opx) return null;
      const sumM = (r) => (r ? monthsArr(r.months).reduce((a, b) => a + Math.abs(num(b)), 0) : 0);
      return (sumM(per) + sumM(opx)) / 12;
    };
    const burnN1 = annualFixedMonthly(plPrev);
    const burnN2 = annualFixedMonthly(plPrev2);
    const burnBars = [
      { label: String(year - 2), val: burnN2 },
      { label: String(year - 1), val: burnN1 },
      { label: String(year), val: burn != null ? Math.abs(burn) : null },
    ].filter((b) => b.val != null);

    // ── Signaux (réels uniquement) ────────────────────────────────────────
    const signals = [];
    const r3r = r3 == null ? null : Math.round(r3 * 10) / 10;
    if (r3r != null && r3r < 1.5) signals.push(tp('phrases:p_79c84740a2', { val: r3r.toFixed(1).replace('.', ',') }));
    if (dso != null && dso > 45) signals.push(tp('phrases:p_88b2739939', { days: Math.round(dso) }));
    // Trésorerie exprimée en MOIS DE SALAIRE (masse salariale brute + charges du
    // dernier mois clôturé) : combien de paies la trésorerie couvre. Alerte si < 2.
    const monthsSalary = (payroll > 0 && liq != null) ? liq / payroll : null;
    if (monthsSalary != null && monthsSalary < 2) signals.push(tp('phrases:p_c2a9deedaf', { months: monthsSalary.toFixed(1).replace('.', ',') }));
    if (creditLimit > 0 && liqReal.length && Math.min(...liqReal) < -creditLimit) {
      signals.push(p('Solde réel sous la <strong>ligne de crédit</strong> sur au moins un mois'));
    }

    return {
      closed, liqMonthly, liqMonthlyPrev, liq, bfre, tresoBfre, burn, burnComps, burnN1, burnBars, runway, monthsSalary, bfreStackRolling, adv,
      caCum, caCumDelta, caCumEptCap, caEpt, caEptDelta, eptReal, eptPrev, capFte,
      caAnnDisplay, caAnnIsSeasonal, revPrevFull, annGrowth, annDelta, ca12m,
      margeBrute, margeBruteVA, margeEbitda, margeNette, netAnn, caGrowth,
      vaAnn, margeVA, grossAnn, ebitdaAnn, revBars, revCum, hasPrevRev,
      r1, r2, r3, dso, dio, dpo, rec, stock, ap, rolling, signals,
      // Postes bruts additionnels — exposés pour le Cockpit CFO (drawers ratios).
      curAssets: ca, curLiab: cl, revYtd,
    };
}
